import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  PageTitle,
  SaveState,
  Select,
  Spinner,
  Textarea,
} from '../components/ui.jsx';

const BLANK = {
  title: '',
  client: '',
  tagline: '',
  summary: '',
  industry: '',
  region: '',
  year: new Date().getFullYear(),
  status: 'DRAFT',
  isFeatured: false,
  order: 0,
  metrics: [],
  seo: { metaTitle: '', metaDescription: '', ogImageUrl: '', canonicalUrl: '', noindex: false },
};

function MetricsEditor({ metrics, onChange }) {
  const rows = Array.isArray(metrics) ? metrics : [];

  function update(index, key, value) {
    const next = rows.map((row, i) => (i === index ? { ...row, [key]: value } : row));
    onChange(next);
  }

  return (
    <div className="space-y-3">
      {rows.map((row, index) => (
        <div key={index} className="flex gap-3">
          <Input
            placeholder="Label (e.g. Engagement)"
            value={row.label ?? ''}
            onChange={(e) => update(index, 'label', e.target.value)}
          />
          <Input
            placeholder="Value (e.g. +240%)"
            value={row.value ?? ''}
            onChange={(e) => update(index, 'value', e.target.value)}
          />
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          >
            ×
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => onChange([...rows, { label: '', value: '' }])}
      >
        + Add metric
      </Button>
    </div>
  );
}

function Editor({ item, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...BLANK, ...item, seo: { ...BLANK.seo, ...(item?.seo ?? {}) } });
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);

  const isNew = !item?.id;

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function save(event) {
    event.preventDefault();
    setState('saving');
    setError(null);

    // Only send fields the API accepts — extra keys from the read model
    // (cover, services, timestamps) would fail validation.
    const payload = {
      title: form.title,
      client: form.client,
      tagline: form.tagline || null,
      summary: form.summary,
      industry: form.industry || null,
      region: form.region || null,
      year: form.year ? Number(form.year) : null,
      status: form.status,
      isFeatured: Boolean(form.isFeatured),
      order: Number(form.order) || 0,
      metrics: form.metrics ?? [],
      body: form.body ?? null,
      // Blank strings are sent as null so the renderer falls back to the
      // title/summary rather than publishing an empty meta tag.
      seo: Object.fromEntries(
        Object.entries(form.seo ?? {}).map(([k, v]) =>
          typeof v === 'string' ? [k, v.trim() === '' ? null : v.trim()] : [k, v],
        ),
      ),
    };

    try {
      if (isNew) await api.post('/api/admin/case-studies', payload);
      else await api.patch(`/api/admin/case-studies/${item.id}`, payload);

      setState('saved');
      onSaved();
    } catch (err) {
      setState('error');
      setError(err.message);
    }
  }

  return (
    <Card className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl">{isNew ? 'New case study' : 'Edit case study'}</h2>
        <SaveState state={state} />
      </div>

      <ErrorNote error={error} />

      <form onSubmit={save} className="space-y-6">
        <div className="grid gap-5 md:grid-cols-2">
          <Field label="Title" htmlFor="title" required>
            <Input
              id="title"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              required
            />
          </Field>
          <Field label="Client" htmlFor="client" required>
            <Input
              id="client"
              value={form.client}
              onChange={(e) => set('client', e.target.value)}
              required
            />
          </Field>
        </div>

        <Field label="Tagline" htmlFor="tagline" hint="One line, shown under the title.">
          <Input
            id="tagline"
            value={form.tagline ?? ''}
            onChange={(e) => set('tagline', e.target.value)}
          />
        </Field>

        <Field label="Summary" htmlFor="summary" required hint="Used on cards and as the meta description.">
          <Textarea
            id="summary"
            rows={3}
            value={form.summary}
            onChange={(e) => set('summary', e.target.value)}
            required
          />
        </Field>

        <div className="grid gap-5 md:grid-cols-3">
          <Field label="Industry" htmlFor="industry">
            <Input
              id="industry"
              value={form.industry ?? ''}
              onChange={(e) => set('industry', e.target.value)}
            />
          </Field>
          <Field label="Region" htmlFor="region">
            <Input
              id="region"
              value={form.region ?? ''}
              onChange={(e) => set('region', e.target.value)}
            />
          </Field>
          <Field label="Year" htmlFor="year">
            <Input
              id="year"
              type="number"
              value={form.year ?? ''}
              onChange={(e) => set('year', e.target.value)}
            />
          </Field>
        </div>

        <Field label="Headline metrics" hint="Shown as the stat row on the case study page.">
          <MetricsEditor metrics={form.metrics} onChange={(next) => set('metrics', next)} />
        </Field>

        <div className="grid items-end gap-5 md:grid-cols-3">
          <Field label="Status" htmlFor="status">
            <Select id="status" value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="DRAFT">Draft</option>
              <option value="PUBLISHED">Published</option>
              <option value="ARCHIVED">Archived</option>
            </Select>
          </Field>
          <Field label="Order" htmlFor="order" hint="Lower shows first.">
            <Input
              id="order"
              type="number"
              value={form.order}
              onChange={(e) => set('order', e.target.value)}
            />
          </Field>
          <Checkbox
            label="Feature on home page"
            checked={Boolean(form.isFeatured)}
            onChange={(e) => set('isFeatured', e.target.checked)}
          />
        </div>

        <div className="border-t-3 border-ink pt-6">
          <h3 className="text-lg">Search & social</h3>
          <p className="mt-1 text-sm text-muted">
            Leave blank to use the title and summary above. Only override when you want
            different wording for search results or a shared link.
          </p>

          <div className="mt-5 space-y-5">
            <Field
              label="Search title"
              htmlFor="seo-title"
              hint="Around 60 characters; longer gets truncated by Google."
            >
              <Input
                id="seo-title"
                value={form.seo?.metaTitle ?? ''}
                onChange={(e) => set('seo', { ...form.seo, metaTitle: e.target.value })}
              />
            </Field>

            <Field
              label="Search description"
              htmlFor="seo-desc"
              hint="Around 155 characters."
            >
              <Textarea
                id="seo-desc"
                rows={3}
                value={form.seo?.metaDescription ?? ''}
                onChange={(e) => set('seo', { ...form.seo, metaDescription: e.target.value })}
              />
            </Field>

            <Field
              label="Share image URL"
              htmlFor="seo-og"
              hint="1200x630. Defaults to the cover image, then the site card."
            >
              <Input
                id="seo-og"
                value={form.seo?.ogImageUrl ?? ''}
                onChange={(e) => set('seo', { ...form.seo, ogImageUrl: e.target.value })}
                placeholder="https://…"
              />
            </Field>

            <Checkbox
              label="Hide from search engines (noindex)"
              checked={Boolean(form.seo?.noindex)}
              onChange={(e) => set('seo', { ...form.seo, noindex: e.target.checked })}
            />
          </div>
        </div>

        <div className="flex gap-3 border-t-3 border-ink pt-6">
          <Button type="submit" disabled={state === 'saving'}>
            {form.status === 'PUBLISHED' ? 'Save & publish' : 'Save draft'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default function CaseStudies() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  function load() {
    setError(null);
    api
      .get('/api/admin/case-studies?perPage=100')
      .then((data) => setItems(data.items))
      .catch((err) => setError(err.message));
  }

  useEffect(load, []);

  async function remove(item) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/case-studies/${item.id}`);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  if (editing !== null) {
    return (
      <Editor
        item={editing}
        onSaved={() => {
          setEditing(null);
          load();
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <>
      <PageTitle
        title="Case studies"
        description="Published case studies appear on /work and the home page within seconds."
        action={<Button onClick={() => setEditing({})}>+ New case study</Button>}
      />

      <ErrorNote error={error} onRetry={load} />

      {!items ? (
        <Spinner />
      ) : items.length === 0 ? (
        <EmptyState
          title="No case studies yet"
          description="Add the first one and it will appear on the public site immediately."
          action={<Button onClick={() => setEditing({})}>+ New case study</Button>}
        />
      ) : (
        <Card>
          <table className="w-full text-left text-sm">
            <thead className="border-b-3 border-ink">
              <tr>
                <th className="p-4 text-xs uppercase tracking-widest">Title</th>
                <th className="p-4 text-xs uppercase tracking-widest">Client</th>
                <th className="p-4 text-xs uppercase tracking-widest">Status</th>
                <th className="p-4 text-xs uppercase tracking-widest">Featured</th>
                <th className="p-4" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-line last:border-0">
                  <td className="p-4 font-semibold">{item.title}</td>
                  <td className="p-4 text-muted">{item.client}</td>
                  <td className="p-4">
                    <Badge tone={item.status}>{item.status}</Badge>
                  </td>
                  <td className="p-4">{item.isFeatured ? 'Yes' : '—'}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setEditing(item)}>
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => remove(item)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
